import { Permission, PermissionsService } from '@fitup-monorepo/core/lib/services/permissions/permissions.service';
import { hasSelectableTeam } from '@fitup-monorepo/core/lib/util/wizard.util';
import { firstValueFrom } from 'rxjs';
import { selectTeamCustomer } from '@fitup-monorepo/core/lib/state/profile/profile.selector';
import { ToastService } from '@fitup-monorepo/core/lib/services/toast/toast.service';
import { TeamSelectionService } from '@fitup-monorepo/core/lib/services/team-selection/team-selection.service';
import {
  PromoCodeApplyService,
  showPromocodeOnboardingPage,
} from '@fitup-monorepo/onboarding/lib/promo-code/promo-code-apply.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OnboardingStep, OnboardingWizardService } from '@fitup-monorepo/core/lib/services/wizard/wizard.service';
import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@fitup-monorepo/core/lib/services/local-storage.service';
import { AppRoutes } from '@fitup-monorepo/routes/lib/app-routes';

@Injectable({
  providedIn: 'root',
})
export class FitupOnboardingWizardService extends OnboardingWizardService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly toastService = inject(ToastService);
  private readonly teamSelectionService = inject(TeamSelectionService);
  readonly permissionsService: PermissionsService;
  private readonly promoCodeApplyService = inject(PromoCodeApplyService);
  readonly router: Router;
  readonly activatedRoute: ActivatedRoute;
  private readonly store = inject(Store);

  private readonly onboardingFlow: OnboardingStep[] = [
    {
      index: 0,
      route: AppRoutes.START_ONBOARD,
      isShortSignupStep: true,
    },
    {
      index: 20,
      route: AppRoutes.PROMO_CODE_ONBOARD,
      isVisible: () => showPromocodeOnboardingPage(this.store, this.localStorageService, this.promoCodeApplyService),
      isShortSignupStep: true,
    },
    {
      index: 30,
      route: AppRoutes.ACCOUNT_DETAILS_EDITOR,
      isShortSignupStep: true,
    },
    {
      index: 40,
      route: AppRoutes.GENDER_AND_BIRTHDATE,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 50,
      route: AppRoutes.MEASUREMENTS,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 60,
      route: AppRoutes.FITNESS_GOALS,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 70,
      route: AppRoutes.FITNESS_LEVEL,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 80,
      route: AppRoutes.INTERESTS,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 90,
      route: AppRoutes.EDIT_PROFILE_PICTURE,
      isShortSignupStep: false,
    },
    {
      index: 100,
      route: AppRoutes.TEAM_SELECTION_ONBOARD(),
      permission: Permission.teamChallenge,
      isVisible: () => hasSelectableTeam(this.teamSelectionService, this.toastService),
      isShortSignupStep: false,
    },
    {
      index: 110,
      route: AppRoutes.CONGRATULATIONS,
      permission: Permission.teamChallenge,
      isVisible: async (): Promise<boolean> => {
        const teamCustomer = await firstValueFrom(this.store.select(selectTeamCustomer));
        return !!teamCustomer?.team;
      },
      isShortSignupStep: false,
    },
    {
      index: 120,
      route: AppRoutes.REMINDER,
      permission: Permission.fitAtWorkReminder,
      isShortSignupStep: false,
    },
    {
      index: 130,
      route: AppRoutes.FINALIZE_ONBOARD,
      permission: Permission.basic,
      isShortSignupStep: false,
    },
    {
      index: 140,
      route: AppRoutes.SUCCESS_ONBOARD,
      isShortSignupStep: false,
    },
  ];

  constructor() {
    const permissionsService = inject(PermissionsService);
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);

    super(router, activatedRoute, permissionsService);

    this.permissionsService = permissionsService;
    this.router = router;
    this.activatedRoute = activatedRoute;
  }

  public getSteps(): OnboardingStep[] {
    return this.onboardingFlow;
  }
}
